import { handleSessionResponse, SessionExpiredError, SessionRetryError } from "@/core/services/middleware/SessionAuthentication";
import type { IExceptionAPIClient } from "@/core/services/exceptions/types";
import type { IF360Date } from "@/core/plugins/date/F360Date";
import { F360DayJs } from "@/core/plugins/dayjs/DayJs";
import { F360Lodash } from "@/core/plugins/lodash/Lodash";
import { F360CurrencyJs } from "../plugins/currency.js/Currencyjs";

import type { IF360Utility } from "../plugins/lodash/types";
import type { IF360Currency } from "../plugins/currency.js/types";
import ToastificationNotificationException from "@/core/services/exceptions/ToastificationNotificationException";
//import { captureException } from "@sentry/vue";

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getCurrentExceptionNotification = (): IExceptionAPIClient =>
  new ToastificationNotificationException();

export const getCurrentDateLib = (): IF360Date => new F360DayJs();
export const getCurrentUtilityLib = (): IF360Utility => new F360Lodash();
export const getCurrentCurrencyFormatterLib = (): IF360Currency =>
  new F360CurrencyJs();

export const CNPJ_UNMASKED_LENGTH = 14;
export const CNPJ_REGEX = /^[A-Z0-9]{12}\d{2}$/;

export const removeCNPJMask = (cnpj: string | null | undefined) =>
  (cnpj || "").replace(/[^a-zA-Z0-9]+/g, "").toUpperCase();

export const applyCNPJMask = (cnpj: string) =>
  removeCNPJMask(cnpj).replace(
    /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );

export const isCNPJFormatValid = (cnpj: string | null | undefined) =>
  CNPJ_REGEX.test(removeCNPJMask(cnpj));

export const hasData = <T>(data: T | null | undefined, defaultValue: T): T => {
  const hasntData = Array.isArray(data) && data.length === 0;

  if (!data || hasntData) return defaultValue;

  return data;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
export const asyncWrap = async <T>(
  promise: Promise<T>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  exceptionHandle?: () => void
): Promise<[T | null, null | unknown]> => {
  try {
    const data = await promise;

    return [data, null];
  } catch (_err) {
    // Removi os alertas de erro temporariamento para o biglançamento
    // if (exceptionHandle) exceptionHandle()

    // captureException(
    //   new Error("http", {
    //     cause: err
    //   })
    // );

    return [null, _err];
  }
};

//@arao
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionFilter<T extends object> = (val: T) => any;

export const groupBy = <T extends object>(
  arr: Array<T>,
  fn: FunctionFilter<T>
): Record<string, T[]> => {
  return arr
    .map(typeof fn === "function" ? fn : (val) => val[fn])
    .reduce((acc, val, i) => {
      acc[val] = (acc[val] || []).concat(arr[i]);

      return acc;
    }, {});
};

type ValueOrUndefined<T> = T | undefined;

type ObserverState<T> = {
  [K in keyof T]: ValueOrUndefined<T[K]>;
};

export const clearObject = <T>(observerState: ObserverState<T>) => {
  Object.keys(observerState).forEach((key) => {
    observerState[key as keyof typeof observerState] = undefined;
  });
};

export const capitalizeFirstLetter = (string: string | null) => {
  if (!string) return "";

  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const exportarBlob = async (
  downloadUrl: string,
  fetchInit: RequestInit = {},
  authenticated = false
): Promise<boolean> => {
  try {
    //somente trazendo do finanças essa mesma funcção para teste
    const response = await fetch(downloadUrl, fetchInit);
    if (authenticated) {
      await handleSessionResponse(
        response.status,
        fetchInit.method || "GET",
        downloadUrl,
        response.headers.get("X-Location")
      );
    }

    if (!response.ok) {
      return false;
    }

    // Obtém o nome e extensão do arquivo pelo cabeçalho 'Content-Disposition'
    const contentDispositionHeader = response.headers.get(
      "Content-Disposition"
    );
    if (!contentDispositionHeader) {
      return false;
    }

    const filename = contentDispositionHeader
      .split(";")
      .find((part) => part.trim().startsWith("filename="))
      ?.split("=")[1]
      ?.replace(/["']/g, "");

    if (!filename) {
      return false;
    }

    const blob = await response.blob();

    // Cria o link e baixa o blob
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    // Limpa após o download
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    return true;
  } catch (error) {
    if (error instanceof SessionExpiredError || error instanceof SessionRetryError) {
      throw error;
    }

    return false;
  }
};

export const getStringDCTFWeb = (periodo: Date | undefined) => {
  if (!periodo) return "DCTF";
  const dataPeriodo = new Date(periodo);
  const limite = new Date(2025, 0, 1);

  return dataPeriodo >= limite ? "DCTFWeb" : "DCTF";
};

export const handleTextEllipsis = (text: string, limit: number) => {
  if (text.length > limit) {
    return text.slice(0, limit - 1) + "...";
  }

  return text;
};

export const parseDecimal = (str: string): number => {
  if (str == null || str === "") return 0;
  const s = String(str).trim();
  if (s.includes(",")) {
    const br = s.replace(/\./g, "").replace(",", ".");

    return Number.parseFloat(br) || 0;
  }

  return Number.parseFloat(s.replace(/\s/g, "")) || 0;
};

export const downloadBlob = async (
  blob: Blob,
  fileName: string
): Promise<boolean> => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch {
    return false;
  }
};

export const queryParamToString = <T>(
  value: T | T[] | undefined | null
): T | undefined => {
  if (value == null || value === "") return undefined;

  return Array.isArray(value) ? value[0] : value;
};
